import type { NavigateOptions, ParsedLocation } from '@tanstack/react-router';
import type { SignupSearchParams } from 'router/search/signup';

import type { AppRouterContext } from 'providers/AppRouter';
import {
  findBestNextStep,
  findStepIndex,
  findStepPath,
} from 'hooks/useSignupSteps/utils';
import type { Steps } from 'hooks/useSignupSteps/useSignupSteps';
import { readChallengeHash } from 'services/partnersSelfSignup/otp';
import {
  type GetLeadResponse,
  getLead,
  getLeadQueryKey,
  readLeadQueryCache,
} from 'services/partnersSelfSignup/signup';
import { experimentVariantManager } from 'utils/experimentation';

type SignupOnVerifiedParams = {
  context: Pick<AppRouterContext, 'queryClient' | 'steps'>;
  leadId: string;
  email: string;
  previousLocation: ParsedLocation<SignupSearchParams> | undefined;
};

/**
 * Where the signup sends a Partner whose email is verified: the best step for
 * their Lead, as when a session is restored. Does the signup's work for that
 * (Lead recovery: reads the Lead under the hash and writes it over the working
 * copy, keeping the fields only the working copy holds) and returns the step
 * to go to.
 *
 * Throws when its work fails, including when the BFF does not accept the hash
 * (the loop rule's host part, see `OnVerified`). It always reads the server,
 * because the working copy proves nothing about the hash, and the Lead read
 * fails with a 401 when the BFF refuses it. Without that, OTP moving on by
 * itself would loop.
 */
export function createSignupOnVerified({
  context,
  leadId,
  email,
  previousLocation,
}: SignupOnVerifiedParams) {
  const { queryClient, steps: signupSteps } = context;

  return async (): Promise<NavigateOptions> => {
    const hash = readChallengeHash({ queryClient, email });
    if (!hash) {
      throw new Error('[onVerified] No challenge for the email');
    }

    const serverLead = await getLead({ leadId, emailValidationId: hash });
    experimentVariantManager.mergeRecovered(serverLead.lead.experimentVariant);

    const workingCopy = readLeadQueryCache(queryClient, { leadId });
    const lead: GetLeadResponse = {
      ...serverLead,
      step: (serverLead.lastStepSubmitted ?? 1) + 1,
      lead: {
        // need this to avoid losing the lead data that currently doesn't come from the API
        ...workingCopy?.lead,
        ...serverLead.lead,
      },
    };
    queryClient.setQueryData(getLeadQueryKey({ leadId }), lead);

    const selectedSteps = signupSteps.steps;

    const locationPreviousStep = findStepIndex(
      selectedSteps,
      previousLocation?.pathname ?? '/',
    );

    const nextStep = findBestNextStep(
      selectedSteps,
      lead.stepName as Steps[number]['name'],
    );

    const to = findStepPath(
      selectedSteps,
      nextStep > locationPreviousStep ? nextStep : locationPreviousStep + 1,
    );

    if (!to) {
      throw new Error('[onVerified] No step to go to after email validation');
    }

    return {
      to,
      replace: true,
      state: { shouldShowRecoveryLeadModal: true },
    };
  };
}
